import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  ArrowLeft,
  Loader,
  TrendingUp,
  CheckCircle,
  Activity,
  AlertCircle,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { analyticsApi } from "../lib";

type TrendPoint = {
  _id: string;
  enrollments: number;
  completed: number;
  active: number;
};

const AdminEnrollmentTrends = () => {
  const [data, setData] = useState<TrendPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<"7d" | "30d" | "90d">("7d");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await analyticsApi.getEnrollmentTrends(range);
        console.log("Enrollment trends response:", res);
        const items = Array.isArray((res as any)?.data) ? (res as any).data : [];
        if (cancelled) return;
        if (items.length) {
          setData(
            items.map((item) => ({
              ...item,
              date: new Date(item._id).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              }),
            })),
          );
        } else if (range === "7d") {
          setRange("30d");
        } else {
          setData([]);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load enrollment trends", err);
        setError(err instanceof Error ? err.message : "Failed to load enrollment trends");
        setData([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [range]);

  const totals = data.reduce(
    (acc, item) => ({
      enrollments: acc.enrollments + item.enrollments,
      completed: acc.completed + item.completed,
      active: acc.active + item.active,
    }),
    { enrollments: 0, completed: 0, active: 0 },
  );

  return (
    <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
      <div className="px-4 mx-auto max-w-7xl md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <Link
                to="/dashboard/admin"
                className="p-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:border-orange-500 hover:text-orange-600"
              >
                <ArrowLeft className="w-4 h-4" />
              </Link>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 shadow-sm">
                    <TrendingUp className="w-4 h-4 text-white" />
                  </div>
                  <h1 className="text-lg font-bold text-gray-900 md:text-xl">
                    Enrollment Trends
                  </h1>
                </div>
                <p className="text-[10px] text-gray-500 ml-10 md:text-xs">
                  Live enrollment activity for the selected period
                </p>
              </div>
            </div>
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
              {(["7d", "30d", "90d"] as const).map((option) => (
                <button
                  key={option}
                  onClick={() => setRange(option)}
                  className={`px-3 py-1.5 text-[10px] font-medium rounded-md transition-all ${
                    range === option
                      ? "bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-sm"
                      : "text-gray-600 hover:text-orange-600"
                  }`}
                >
                  {option === "7d" ? "7 Days" : option === "30d" ? "30 Days" : "90 Days"}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center min-h-[200px]">
            <Loader className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
        ) : error ? (
          <div className="p-4 border border-red-200 rounded-lg bg-red-50">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <p className="text-sm font-semibold text-red-700">Failed to load enrollment trends</p>
            </div>
            <p className="text-xs text-red-600">{error}</p>
          </div>
        ) : data.length === 0 ? (
          <div className="p-4 border border-gray-100 rounded-lg bg-white">
            <p className="text-sm text-gray-600">No enrollment data found for the selected range.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-white border border-gray-100 rounded-xl shadow-sm">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-orange-500" />
                  <p className="text-[10px] text-gray-500">Total Enrollments</p>
                </div>
                <p className="text-sm font-bold text-gray-900">
                  {totals.enrollments.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-white border border-gray-100 rounded-xl shadow-sm">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <p className="text-[10px] text-gray-500">Completed</p>
                </div>
                <p className="text-sm font-bold text-gray-900">
                  {totals.completed.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-white border border-gray-100 rounded-xl shadow-sm">
                <div className="flex items-center gap-2 mb-1">
                  <Activity className="w-4 h-4 text-blue-500" />
                  <p className="text-[10px] text-gray-500">Active</p>
                </div>
                <p className="text-sm font-bold text-gray-900">
                  {totals.active.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Charts */}
            <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl md:p-4">
              <h2 className="text-[11px] font-semibold text-gray-900 mb-2 md:text-sm md:mb-3">
                Daily Enrollment Trend
              </h2>
              <div className="h-48 md:h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#9ca3af" />
                    <YAxis tick={{ fontSize: 10 }} stroke="#9ca3af" />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Line
                      type="monotone"
                      dataKey="enrollments"
                      name="Enrollments"
                      stroke="#F59E0B"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl md:p-4">
              <h2 className="text-[11px] font-semibold text-gray-900 mb-2 md:text-sm md:mb-3">
                Status Breakdown by Day
              </h2>
              <div className="h-48 md:h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#9ca3af" />
                    <YAxis tick={{ fontSize: 10 }} stroke="#9ca3af" />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Bar dataKey="active" name="Active" fill="#3B82F6" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="completed" name="Completed" fill="#10B981" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEnrollmentTrends;
